import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useUpdateCompanyData, CompanyData } from '@/hooks/use-company-data';
import { Loader2 } from 'lucide-react';

interface CompanyDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  initialData?: CompanyData | null;
  isRequired?: boolean;
}

const ESTADOS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

export function CompanyDataModal({ isOpen, onClose, onSaved, initialData, isRequired = false }: CompanyDataModalProps) {
  const [formData, setFormData] = useState({
    email: initialData?.email || '',
    telefone: initialData?.telefone || '',
    doc_cnpj: initialData?.doc_cnpj || '',
    endereco_logradouro: initialData?.endereco_logradouro || '',
    endereco_numero: initialData?.endereco_numero || '',
    endereco_bairro: initialData?.endereco_bairro || '',
    endereco_cidade: initialData?.endereco_cidade || '',
    endereco_estado: initialData?.endereco_estado || '',
    endereco_cep: initialData?.endereco_cep || ''
  });

  const { toast } = useToast();
  const updateCompanyMutation = useUpdateCompanyData();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const formatCEP = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/^(\d{5})(\d{3})$/, '$1-$2');
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  };

  const handleSave = async () => {
    try {
      await updateCompanyMutation.mutateAsync({
        email: formData.email,
        telefone: formData.telefone,
        doc_cnpj: formData.doc_cnpj,
        endereco_logradouro: formData.endereco_logradouro,
        endereco_numero: formData.endereco_numero,
        endereco_bairro: formData.endereco_bairro,
        endereco_cidade: formData.endereco_cidade,
        endereco_estado: formData.endereco_estado,
        endereco_cep: formData.endereco_cep
      });

      // Wait a bit for cache to be updated
      await new Promise(resolve => setTimeout(resolve, 300));

      toast({
        title: "Dados salvos com sucesso",
        description: "Os dados da empresa foram atualizados.",
      });

      onSaved();
      onClose();
    } catch (error) {
      console.error('Error saving company data:', error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar os dados da empresa.",
        variant: "destructive",
      });
    }
  };

  const isFormValid = formData.email && formData.telefone && formData.doc_cnpj && 
                     formData.endereco_logradouro && formData.endereco_numero && 
                     formData.endereco_bairro && formData.endereco_cidade && 
                     formData.endereco_estado && formData.endereco_cep;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isRequired ? 'Complete os Dados da Empresa' : 'Dados da Empresa'}
          </DialogTitle>
          <DialogDescription>
            {isRequired 
              ? 'Complete os dados da empresa. Você pode fechar este modal, mas ele reaparecerá no próximo login até que todos os dados sejam preenchidos.'
              : 'Complete os dados da empresa para prosseguir com o pagamento.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="email@empresa.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="telefone">Telefone</Label>
            <Input
              id="telefone"
              value={formData.telefone}
              onChange={(e) => handleInputChange('telefone', formatPhone(e.target.value))}
              placeholder="(00) 00000-0000"
              maxLength={15}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="doc_cnpj">CNPJ</Label>
            <Input
              id="doc_cnpj"
              value={formData.doc_cnpj}
              onChange={(e) => handleInputChange('doc_cnpj', formatCNPJ(e.target.value))}
              placeholder="00.000.000/0000-00"
              maxLength={18}
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="endereco_logradouro">Logradouro</Label>
              <Input
                id="endereco_logradouro"
                value={formData.endereco_logradouro}
                onChange={(e) => handleInputChange('endereco_logradouro', e.target.value)}
                placeholder="Rua, Av., etc."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endereco_numero">Número</Label>
              <Input
                id="endereco_numero"
                value={formData.endereco_numero}
                onChange={(e) => handleInputChange('endereco_numero', e.target.value)}
                placeholder="123"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="endereco_bairro">Bairro</Label>
            <Input
              id="endereco_bairro"
              value={formData.endereco_bairro}
              onChange={(e) => handleInputChange('endereco_bairro', e.target.value)}
              placeholder="Bairro"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="endereco_cidade">Cidade</Label>
              <Input
                id="endereco_cidade"
                value={formData.endereco_cidade}
                onChange={(e) => handleInputChange('endereco_cidade', e.target.value)}
                placeholder="Cidade"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endereco_estado">Estado</Label>
              <select
                id="endereco_estado"
                value={formData.endereco_estado}
                onChange={(e) => handleInputChange('endereco_estado', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Selecione</option>
                {ESTADOS_BR.map(estado => (
                  <option key={estado} value={estado}>{estado}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="endereco_cep">CEP</Label>
            <Input
              id="endereco_cep"
              value={formData.endereco_cep}
              onChange={(e) => handleInputChange('endereco_cep', formatCEP(e.target.value))}
              placeholder="00000-000"
              maxLength={9}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {isRequired ? 'Fechar' : 'Cancelar'}
          </Button>
          <Button
            onClick={handleSave} 
            disabled={!isFormValid || updateCompanyMutation.isPending}
          >
            {updateCompanyMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar e Continuar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}